/**
 * Outlet Thunks
 * Async thunks for outlet management operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { AccessControlCode, FULL_ACCESS_USER_ROLES } from '@shared/constants';
import { SubscriptionPackage, UserRole } from '../../../shared/types';
import {
  createOutlet as createOutletService,
  deleteOutlet as deleteOutletService,
  fetchCompanyOutlets,
  fetchOutletsWithStats,
  getOutletLimits as getOutletLimitsService,
  updateOutlet as updateOutletService,
} from '../../services/outletService';
import {
  CreateOutletRequest,
  Outlet,
  OutletLimits,
  OutletWithStats,
  UpdateOutletRequest,
} from '../../types/outlet';
import type { RootState } from '..';

interface CreateOutletParams {
  request: CreateOutletRequest;
  packageType: SubscriptionPackage;
}

interface FetchOutletLimitsParams {
  companyId: number;
  packageType: SubscriptionPackage;
}

const getScopedOutletId = (state: RootState): string | null => {
  const role = state.auth.user?.role ?? null;
  const { accessControlCodes } = state.auth;
  const authOutletId = state.auth.user?.outlet?.id ?? null;

  const hasCompanyScope = Boolean(
    (role && FULL_ACCESS_USER_ROLES.includes(role as UserRole)) ||
    accessControlCodes.includes(
      AccessControlCode.DASHBOARD_MULTIPLE_OUTLETS_ACCESS,
    ),
  );

  return hasCompanyScope ? null : authOutletId;
};

export const fetchOutlets = createAsyncThunk<
  Outlet[],
  number,
  { rejectValue: string }
>('outlets/fetchOutlets', async (companyId, { getState, rejectWithValue }) => {
  try {
    const outletId = getScopedOutletId(getState() as RootState);
    const response = await fetchCompanyOutlets(companyId, outletId);
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to fetch outlets');
    }
    return response.data || [];
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch outlets',
    );
  }
});

export const fetchOutletsWithStatsThunk = createAsyncThunk<
  OutletWithStats[],
  number,
  { rejectValue: string }
>(
  'outlets/fetchOutletsWithStats',
  async (companyId, { getState, rejectWithValue }) => {
    try {
      const outletId = getScopedOutletId(getState() as RootState);
      const response = await fetchOutletsWithStats(companyId, outletId);
      if (!response.success) {
        return rejectWithValue(
          response.error || 'Failed to fetch outlets with stats',
        );
      }
      return response.data || [];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch outlets with stats',
      );
    }
  },
);

export const fetchOutletLimits = createAsyncThunk<
  OutletLimits,
  FetchOutletLimitsParams,
  { rejectValue: string }
>(
  'outlets/fetchOutletLimits',
  async ({ companyId, packageType }, { rejectWithValue }) => {
    try {
      const response = await getOutletLimitsService(companyId, packageType);
      if (!response.success || !response.data) {
        return rejectWithValue(
          response.error || 'Failed to fetch outlet limits',
        );
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch outlet limits',
      );
    }
  },
);

export const createOutlet = createAsyncThunk<
  Outlet,
  CreateOutletParams,
  { rejectValue: string }
>(
  'outlets/createOutlet',
  async ({ request, packageType }, { rejectWithValue }) => {
    try {
      const response = await createOutletService(request, packageType);
      if (!response.success || !response.data) {
        return rejectWithValue(response.error || 'Failed to create outlet');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create outlet',
      );
    }
  },
);

export const updateOutlet = createAsyncThunk<
  Outlet,
  UpdateOutletRequest,
  { rejectValue: string }
>('outlets/updateOutlet', async (request, { rejectWithValue }) => {
  try {
    const response = await updateOutletService(request);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to update outlet');
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to update outlet',
    );
  }
});

export const deleteOutlet = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('outlets/deleteOutlet', async (outletId, { rejectWithValue }) => {
  try {
    const response = await deleteOutletService(outletId);
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to delete outlet');
    }
    return outletId;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to delete outlet',
    );
  }
});
